import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format } from "date-fns";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import DatePickerField from "./form/DatePickerField";
import PhoneField from "./form/PhoneField";
import EmailField from "./form/EmailField";
import VenueAddressField from "./form/VenueAddressField";
import TimeRangeSlider from "./form/TimeRangeSlider";
import { ArrowRight, Info, Plus, X, Check, Lock, LockOpen } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";
import { TablesInsert } from "@/integrations/supabase/types";
import { getUserTimezone } from "@/lib/timezone";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

// Comprehensive occasion options - inclusive of all cultural & business events
const OCCASIONS = [
  // Wedding Events
  "Wedding Reception", "Wedding Ceremony", "Engagement Party", "Rehearsal Dinner", 
  "Vow Renewal", "Elopement Celebration", "Destination Wedding",
  
  // South Asian Wedding Events
  "Sangeet", "Mehndi", "Haldi", "Baraat", "Milni", "Jaimala", "Pheras", "Vidaai",
  "Reception (Indian)", "Cocktail Night", "Roka", "Sagai", "Tilak", "Chunni Ceremony",
  
  // South Asian Muslim Events
  "Nikkah", "Walima", "Mehndi (Muslim)", "Dholki", "Mayoun", "Rukhsati",
  
  // Sikh Events
  "Anand Karaj", "Milni (Sikh)", "Jaggo", "Chooda Ceremony", "Kaleere",
  
  // Jewish Events
  "Bar Mitzvah", "Bat Mitzvah", "Aufruf", "Tisch", "Bedeken", "Chuppah", 
  "Hora Dance", "Shabbat Dinner", "Brit Milah", "Baby Naming",
  
  // Hispanic/Latino Events
  "Quinceañera", "Quinceañero", "Fiesta de Quince", "Baile de Debutantes",
  "Día de los Muertos Celebration", "Posada", "Boda (Wedding)",
  
  // East Asian Events
  "Chinese Wedding Banquet", "Tea Ceremony", "Lunar New Year Gala", 
  "Moon Festival Celebration", "Korean Paebaek", "Japanese Sake Ceremony",
  
  // African & Caribbean Events
  "Nigerian Traditional Wedding", "Ghanaian Engagement", "Ethiopian Coffee Ceremony",
  "Caribbean Carnival Party", "Juneteenth Celebration", "Kwanzaa Celebration",
  
  // Middle Eastern Events
  "Lebanese Wedding", "Persian Wedding", "Henna Night", "Zaffa Entrance",
  "Iftar Dinner", "Eid Celebration", "Nowruz Celebration",
  
  // Religious Celebrations
  "Diwali Celebration", "Holi Festival", "Navratri Garba", "Durga Puja", 
  "Ganesh Chaturthi", "Onam Celebration", "Pongal Celebration", "Lohri",
  "Christmas Party", "Easter Celebration", "Hanukkah Party",
  
  // Personal Celebrations
  "Birthday Party", "Milestone Birthday (50th, 60th, etc.)", "Sweet Sixteen",
  "Anniversary", "Baby Shower", "Bridal Shower", "Gender Reveal",
  "Graduation Party", "Retirement Party", "Farewell Party",
  
  // Corporate Events
  "Corporate Gala", "Product Launch", "Company Mixer", "Holiday Party",
  "Awards Ceremony", "Conference", "Shareholder Meeting", "Executive Retreat",
  "Team Building Event", "Client Appreciation", "Networking Event",
  "Trade Show", "Grand Opening", "Broker's Open", "Fundraiser Gala",
  
  // Social Gatherings
  "Cocktail Party", "Dinner Party", "Garden Party", "Pool Party", 
  "House Warming", "Reunion", "Block Party", "Charity Event",
  
  // Special Events
  "Parade", "Festival", "Fashion Show", "Art Gallery Opening", 
  "Wine Tasting", "Private Dinner", "Yacht Party", "Rooftop Soiree", 
  "Masquerade Ball", "Black Tie Gala", "White Party", "Themed Party"
];

// Comprehensive vibe/theme/inspiration options
const VIBES = [
  // Destination & Travel Vibes
  "Santorini Sunset", "Lake Como Elegance", "Tuscan Countryside", "Amalfi Coast", 
  "Paris Chic", "Marrakech Nights", "Bali Tropical", "Mykonos Party", 
  "Maldives Luxury", "Ibiza Sunset", "Dubai Opulence", "Monaco Glamour",
  "California Dreaming", "New York Glamour", "London Sophistication", "Tokyo Modern",
  "Havana Nights", "Caribbean Breeze", "Moroccan Oasis", "Greek Island Escape",
  
  // Classic & Elegant Themes
  "Black Tie Affair", "White Tie Gala", "Old Hollywood Glamour", "Great Gatsby",
  "Art Deco Elegance", "Victorian Romance", "Baroque Opulence", "Renaissance Charm",
  "French Château", "English Garden", "Classic Timeless", "Refined Luxury",
  
  // Modern & Contemporary
  "Minimalist Modern", "Industrial Chic", "Urban Loft", "Scandinavian Clean",
  "Contemporary Sleek", "Architectural", "Monochrome", "Geometric Patterns",
  "High-Tech Futuristic", "Neon Nights", "LED Glow", "Cyberpunk",
  
  // Nature & Outdoor
  "Garden Romance", "Enchanted Forest", "Under the Stars", "Rustic Chic",
  "Mountain Lodge", "Desert Oasis", "Beach Vibes", "Lakeside Serenity",
  "Vineyard Romance", "Wildflower Meadow", "Tropical Paradise", "Autumn Harvest",
  "Winter Wonderland", "Spring Blossom", "Summer Soirée", "Sunset Magic",
  
  // Cultural & Heritage
  "Bollywood Glam", "Desi Vibes", "Mughal Royalty", "Rajasthani Elegance",
  "South Indian Traditional", "Punjabi Celebration", "Indo-Western Fusion",
  "Arabian Nights", "Persian Elegance", "Turkish Delight", "Mediterranean",
  "African Heritage", "Caribbean Culture", "Latin Passion", "Asian Zen",
  "Celtic Charm", "Mexican Fiesta", "Brazilian Carnival", "Greek Orthodox",
  
  // Music & Festival Inspired
  "Coachella Festival", "Stagecoach Country", "Jazz Age", "Blues Lounge",
  "Rock & Roll", "Disco Fever", "EDM Rave", "House Music", "Hip-Hop Luxe",
  "Reggae Chill", "Latin Fiesta", "K-Pop Energy", "Woodstock Revival",
  "Unplugged Acoustic", "Classical Concert", "Opera Night", "Symphony Gala",
  
  // Themed Parties
  "Masquerade Ball", "Casino Royale", "Prohibition Era", "Roaring Twenties",
  "Studio 54", "Vintage Retro", "80s Throwback", "90s Nostalgia", "Y2K Aesthetic",
  "Halloween Spooktacular", "Haunted Elegance", "Costume Party", "Fantasy Realm",
  "Fairy Tale", "Royal Ball", "Midnight Garden", "Secret Garden",
  
  // Color Palettes & Aesthetics
  "Gold & Glam", "Rose Gold Romance", "Silver & Ice", "Blush & Burgundy",
  "Navy & Gold", "Emerald Elegance", "Royal Purple", "Coral Sunset",
  "Sage & Terracotta", "Black & White Classic", "Jewel Tones", "Pastels",
  "Earth Tones", "Vibrant Colors", "Muted Neutrals", "Dramatic Dark",
  
  // Mood & Atmosphere
  "Romantic & Intimate", "Fun & Playful", "Sophisticated & Classy", "Wild & Energetic",
  "Relaxed & Casual", "Grand & Luxurious", "Cozy & Warm", "Fresh & Bright",
  "Mystical & Dreamy", "Bold & Dramatic", "Whimsical", "Bohemian Free-Spirit",
  "Candlelit Intimate", "High Energy Dance", "Lounge Vibes", "Celebration Mode",
  
  // Event-Specific
  "Churchill Downs Derby", "Polo Match", "Yacht Party", "Rooftop Soirée",
  "Garden Tea Party", "Brunch Elegance", "Sunset Cocktails", "After Party",
  "VIP Experience", "Red Carpet", "Award Show", "Premiere Night"
];

// Helper to convert time string to minutes, handling overnight (0-5am = next day)
const timeToMinutes = (timeStr: string): number => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  let totalMinutes = hours * 60 + minutes;
  // Treat 0-5am as next day (add 24 hours)
  if (hours >= 0 && hours < 6) {
    totalMinutes += 24 * 60;
  }
  return totalMinutes;
};
const eventDateTimeSchema = z.object({
  date: z.date(),
  loadInTime: z.string().optional(),
  startTime: z.string().min(1, "Start time is required"),
  endTime: z.string().min(1, "End time is required"),
  venueAddress: z.string().min(5, "Venue address is required"),
  venueAddressVerified: z.boolean().default(false),
  venueCoordinates: z.object({
    lng: z.number(),
    lat: z.number()
  }).optional(),
  occasion: z.string().min(2, "Occasion is required"),
  guestCount: z.number().positive("Guest count must be positive"),
  vibes: z.array(z.string()).min(1, "At least one vibe is required!"),
  locked: z.boolean().default(false),
  budgetLow: z.number().min(0).optional(),
  budgetHigh: z.number().min(0).optional(),
  indoorOutdoor: z.enum(["indoor", "outdoor", "both"]).optional(),
  travelAccommodation: z.enum(["yes", "depends", "no"]).optional()
}).refine(data => {
  const start = data.startTime;
  const end = data.endTime;
  if (start && end) {
    return timeToMinutes(end) > timeToMinutes(start);
  }
  return true;
}, {
  message: "End time must be after start time",
  path: ["endTime"]
}).refine(data => {
  const start = data.startTime;
  const loadIn = data.loadInTime;
  if (loadIn && start) {
    return timeToMinutes(start) > timeToMinutes(loadIn);
  }
  return true;
}, {
  message: "Start time must be after load-in time",
  path: ["startTime"]
}).refine(data => {
  const start = data.startTime;
  const end = data.endTime;
  const loadIn = data.loadInTime;
  if (loadIn && start && end) {
    const loadInMins = timeToMinutes(loadIn);
    const startMins = timeToMinutes(start);
    const endMins = timeToMinutes(end);
    return loadInMins < startMins && loadInMins < endMins;
  }
  return true;
}, {
  message: "Load-in time must be before start and end times",
  path: ["loadInTime"]
}).refine(data => {
  // Only validate venue address if other fields are filled (not a new date)
  if (data.venueAddress || data.occasion || data.vibes?.length) {
    return data.venueAddressVerified;
  }
  return true;
}, {
  message: "Please select a valid address from the suggestions",
  path: ["venueAddress"]
});
const formSchema = z.object({
  firstName: z.string().min(1, "First name is required").min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(1, "Last name is required").min(2, "Last name must be at least 2 characters"),
  phone: z.string().default(""),
  phoneVerified: z.boolean().default(false),
  email: z.string().email(),
  emailVerified: z.boolean().default(false),
  // For confirmed dates
  eventDates: z.array(eventDateTimeSchema).max(7).optional(),
  // For planning mode
  potentialEventDates: z.array(z.date()).max(7).optional(),
  venueAddress: z.string().optional(),
  venueAddressVerified: z.boolean().default(false),
  venueCoordinates: z.object({
    lng: z.number(),
    lat: z.number()
  }).optional()
}).refine(data => {
  return data.eventDates && data.eventDates.length > 0;
}, {
  message: "",
  path: ["eventDates"]
});
type FormData = z.infer<typeof formSchema>;
interface ContactFormProps {
  initialData?: FormData;
  shouldAutoSubmit?: boolean;
  initialFormStep?: 1 | 2;
}

// Get or create session ID (using sessionStorage for consistency with EventInfo)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem("session_id");
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem("session_id", sessionId);
  }
  return sessionId;
};

// Get weighted random time range based on probability distribution
// 7am-11am: 20%, 11am-3pm: 20%, 6pm-11pm: 60%
const getWeightedRandomTimeRange = () => {
  const rand = Math.random();
  if (rand < 0.20) {
    return {
      start: "07:00",
      end: "11:00"
    };
  } else if (rand < 0.40) {
    return {
      start: "11:00",
      end: "15:00"
    };
  } else {
    return {
      start: "18:00",
      end: "23:00"
    };
  }
};

// Format time for display (12-hour with AM/PM)
const formatTimeDisplay = (timeStr: string): string => {
  if (!timeStr) return "--:--";
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const nextDay = hours >= 0 && hours < 6 ? ' +1' : '';
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}${nextDay}`;
};
const ContactForm = ({
  initialData,
  shouldAutoSubmit,
  initialFormStep
}: ContactFormProps) => {
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const returnToStep1 = location.state?.returnToStep1 === true;
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingDraft, setIsLoadingDraft] = useState(true);
  const [formStep, setFormStep] = useState<1 | 2>(initialFormStep || 1); // Track which stage of the form we're on
  const formRef = useRef<HTMLFormElement>(null);
  const eventDatesRef = useRef<HTMLDivElement>(null);
  
  // Restore event dates from sessionStorage for browser back navigation
  const getInitialEventDates = () => {
    try {
      const stored = sessionStorage.getItem('event_dates_cache');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return parsed.map((evt: any) => ({
            ...evt,
            date: new Date(evt.date)
          }));
        }
      }
    } catch (e) {
      console.warn('Could not restore event dates from sessionStorage:', e);
    }
    return [];
  };
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    // Ensures values persist when switching between confirmed/planning modes
    defaultValues: initialData || {
      firstName: "",
      lastName: "",
      phone: "",
      phoneVerified: false,
      email: "",
      emailVerified: false,
      eventDates: getInitialEventDates(),
      venueAddress: "",
      venueAddressVerified: false
    }
  });
  const eventDates = form.watch("eventDates") || [];
  const safeEventDates = (eventDates || []).map((evt: any) => {
    if (!evt || !evt.date) return null;
    const dateObj = evt.date instanceof Date ? evt.date : new Date(evt.date as any);
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return null;
    return { ...evt, date: dateObj };
  }).filter(Boolean) as typeof eventDates;

  // Seamlessly convert fields when switching between confirmed and planning modes
  // Store time info to persist it across conversions
  const [savedTimesMap, setSavedTimesMap] = useState<Map<string, {
    loadInTime: string;
    startTime: string;
    endTime: string;
  }>>(new Map());

  // Load draft from database on mount
  useEffect(() => {
    const loadDraft = async () => {
      try {
        const sessionId = getSessionId();
        
        // First check if user is authenticated and load profile data
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id) {
          const { data: profileData } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", session.user.id)
            .maybeSingle();
          
          if (profileData && profileData.name && profileData.email) {
            // Split name into first and last name
            const nameParts = profileData.name.trim().split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || nameParts[0] || '';
            
            // Pre-fill form with profile data
            form.setValue('firstName', firstName);
            form.setValue('lastName', lastName);
            form.setValue('email', profileData.email);
            form.setValue('emailVerified', true);
            
            if (profileData.phone) {
              form.setValue('phone', profileData.phone);
              form.setValue('phoneVerified', true);
            }
            
            // If we're on event-info page (initialFormStep === 2), skip to stage 2
            if (initialFormStep === 2) {
              setFormStep(2);
              setIsLoadingDraft(false);
              return;
            }
          }
        }
        
        const {
          data,
          error
        } = await supabase.from("contact_form_drafts").select("form_data").eq("session_id", sessionId).maybeSingle();
        if (data && !error) {
          const formData = data.form_data as any;

          // Restore saved times map
          if (formData.savedTimesMap) {
            const restoredMap = new Map();
            Object.entries(formData.savedTimesMap).forEach(([key, value]: [string, any]) => {
              restoredMap.set(key, value);
            });
            setSavedTimesMap(restoredMap);
            delete formData.savedTimesMap;
          }

          // Restore dates
          if (formData.eventDates) {
            formData.eventDates = formData.eventDates.map((evt: any) => ({
              ...evt,
              date: new Date(evt.date)
            }));
          }

          // Set all form values (but don't override profile data)
          Object.keys(formData).forEach(key => {
            if (session?.user?.id && ['firstName', 'lastName', 'email', 'phone', 'emailVerified', 'phoneVerified'].includes(key)) {
              // Skip these fields if user is authenticated - use profile data instead
              return;
            }
            form.setValue(key as any, formData[key]);
          });

          // If basic contact info is already filled, navigate to event-info (unless user explicitly came back)
          if (!returnToStep1 && formData.firstName && formData.lastName && (formData.emailVerified || formData.phoneVerified)) {
            if (formStep === 1) {
              navigate("/event-info", {
                state: { formData }
              });
            } else {
              setFormStep(2);
            }
          }
        }
      } catch (error) {
        console.error("Error loading draft:", error);
      } finally {
        setIsLoadingDraft(false);
      }
    };

    // If initialData is provided, use it; otherwise load from DB
    if (initialData) {
      Object.keys(initialData).forEach(key => {
        form.setValue(key as any, initialData[key as keyof typeof initialData]);
      });
      // Check if we should skip to stage 2 (unless user explicitly came back)
      if (!returnToStep1 && initialData.firstName && initialData.lastName && (initialData.emailVerified || initialData.phoneVerified)) {
        if (formStep === 1) {
          navigate("/event-info", {
            state: { formData: initialData }
          });
        } else {
          setFormStep(2);
        }
      }
      setIsLoadingDraft(false);
    } else {
      loadDraft();
    }
  }, []);

  // Sync event dates to sessionStorage for browser back navigation persistence
  useEffect(() => {
    if (eventDates && eventDates.length > 0) {
      try {
        sessionStorage.setItem('event_dates_cache', JSON.stringify(eventDates));
        console.log('📅 Cached event dates to sessionStorage:', eventDates.length, 'dates');
      } catch (e) {
        console.warn('Could not cache event dates to sessionStorage:', e);
      }
    }
  }, [eventDates]);

  // Scroll form into view when advancing to Stage 2
  useEffect(() => {
    if (formStep === 2 && formRef.current) {
      formRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  }, [formStep]);
  // Removed savedTimesMap and hasConfirmedDates conversion logic
  
  const onStage1Complete = async () => {
    // Validate stage 1 fields only
    const firstName = form.getValues("firstName");
    const lastName = form.getValues("lastName");
    const email = form.getValues("email");
    const phone = form.getValues("phone");
    const emailVerified = form.getValues("emailVerified");
    const phoneVerified = form.getValues("phoneVerified");

    // Check if basic fields are filled
    if (!firstName || firstName.length < 2) {
      toast({
        title: "First name required",
        description: "Please enter your first name (at least 2 characters)",
        variant: "destructive"
      });
      return;
    }
    if (!lastName || lastName.length < 2) {
      toast({
        title: "Last name required",
        description: "Please enter your last name (at least 2 characters)",
        variant: "destructive"
      });
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({
        title: "Valid email required",
        description: "Please enter a valid email address",
        variant: "destructive"
      });
      return;
    }
    if (!phone || phone.length < 10) {
      toast({
        title: "Phone number required",
        description: "Please enter a valid phone number",
        variant: "destructive"
      });
      return;
    }

    // Save basic lead info to database
    try {
      const sessionId = getSessionId();
      const leadData = {
        firstName,
        lastName,
        email,
        phone,
        emailVerified: emailVerified || false,
        phoneVerified: phoneVerified || false
      };

      console.log("Saving Stage 1 draft with verification status:", leadData);

      // Save to contact_form_drafts
      const {
        data: existingDraft
      } = await supabase.from("contact_form_drafts").select("id").eq("session_id", sessionId).single();
      if (existingDraft) {
        await supabase.from("contact_form_drafts").update({
          form_data: leadData as any,
          updated_at: new Date().toISOString()
        }).eq("session_id", sessionId);
      } else {
        await supabase.from("contact_form_drafts").insert({
          session_id: sessionId,
          form_data: leadData as any
        });
      }

      // 🎉 Cute celebration for completing Stage 1!
      confetti({
        particleCount: 150,
        spread: 70,
        origin: {
          x: 0.5,
          y: 0.6
        },
        colors: ['#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#f59e0b'],
        startVelocity: 35,
        gravity: 1,
        scalar: 1.2,
        ticks: 200
      });

      // Second burst for extra cuteness
      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 55,
          origin: {
            x: 0,
            y: 0.6
          },
          colors: ['#ec4899', '#f472b6', '#fbcfe8']
        });
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 55,
          origin: {
            x: 1,
            y: 0.6
          },
          colors: ['#8b5cf6', '#a78bfa', '#c4b5fd']
        });
      }, 100);

      // Mark inquiry as in progress so homepage doesn't reset session
      sessionStorage.setItem('inquiry_in_progress', 'true');

      // Progress to stage 2 with a small delay for the animation
      setTimeout(() => {
        toast({
          title: "✨ Great! Information saved",
          description: "Let's continue with your event details"
        });
        navigate("/event-info", { 
          state: { 
            formData: {
              firstName,
              lastName,
              email,
              phone,
              emailVerified,
              phoneVerified
            }
          } 
        });
      }, 300);
    } catch (error) {
      console.error("Error saving lead:", error);
      // Still allow progression even if save fails
      navigate("/event-info", {
        state: {
          formData: form.getValues()
        }
      });
    }
  };
  const onNext = async (data: FormData) => {
    console.log('🎯 [ContactForm] onNext called with data:', data);
    // Get session if available (not required)
    const {
      data: {
        session
      }
    } = await supabase.auth.getSession();

    // Check if all event dates are locked
    if (data.eventDates) {
      const unlockedIndices = data.eventDates.map((evt, i) => !evt.locked ? i : -1).filter(i => i !== -1);
      if (unlockedIndices.length > 0) {
        // Auto-lock any unlocked date/time blocks so users aren't blocked by the extra click
        const updatedDates = data.eventDates.map((evt, i) => unlockedIndices.includes(i) ? {
          ...evt,
          locked: true
        } : evt);
        form.setValue("eventDates", updatedDates, {
          shouldDirty: true
        });
        (data as any).eventDates = updatedDates;
        toast({
          title: "Times saved",
          description: "We auto-saved your event times so you can continue."
        });
      }
    }
    setIsProcessing(true);

    // Save to database
    try {
      const sessionId = getSessionId();

      // Convert savedTimesMap to plain object for JSON storage
      const savedTimesObj: Record<string, any> = {};
      savedTimesMap.forEach((value, key) => {
        savedTimesObj[key] = value;
      });

      // Try to update existing draft first, if not exists then insert
      const {
        data: existingDraft
      } = await supabase.from("contact_form_drafts").select("id").eq("session_id", sessionId).single();
      if (existingDraft) {
        // Update existing draft
        const {
          error
        } = await supabase.from("contact_form_drafts").update({
          form_data: {
            ...data,
            savedTimesMap: savedTimesObj
          } as any,
          updated_at: new Date().toISOString()
        }).eq("session_id", sessionId);
        if (error) {
          console.error("Error updating draft:", error);
          toast({
            title: "Saved locally",
            description: "We couldn't sync your draft yet, but we'll continue."
          });
          // Do not return; proceed to navigation for seamless UX
        }
      } else {
        // Insert new draft
        const {
          error
        } = await supabase.from("contact_form_drafts").insert({
          session_id: sessionId,
          form_data: {
            ...data,
            savedTimesMap: savedTimesObj
          } as any
        });
        if (error) {
          console.error("Error inserting draft:", error);
          toast({
            title: "Saved locally",
            description: "We couldn't start your draft yet, but we'll continue."
          });
          // Do not return; proceed to navigation for seamless UX
        }
      }

    } catch (error) {
      console.error("Error saving draft:", error);
      toast({
        title: "Saved locally",
        description: "We couldn't sync right now, but we'll continue."
      });
      // Do not stop the flow; proceed to celebrate and navigate
    }

    // 🎉 CELEBRATION TIME! 🎉
    // Trigger spectacular confetti celebration
    const celebrateSuccess = () => {
      // Center burst
      confetti({
        particleCount: 150,
        spread: 180,
        origin: {
          y: 0.6
        },
        colors: ['#10b981', '#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6'],
        startVelocity: 45,
        gravity: 1,
        scalar: 1.5
      });

      // Left side cannon
      setTimeout(() => {
        confetti({
          particleCount: 100,
          angle: 60,
          spread: 55,
          origin: {
            x: 0,
            y: 0.6
          },
          colors: ['#f59e0b', '#f97316', '#ef4444', '#ec4899'],
          startVelocity: 55
        });
      }, 100);

      // Right side cannon
      setTimeout(() => {
        confetti({
          particleCount: 100,
          angle: 120,
          spread: 55,
          origin: {
            x: 1,
            y: 0.6
          },
          colors: ['#10b981', '#3b82f6', '#8b5cf6', '#ec4899'],
          startVelocity: 55
        });
      }, 100);

      // Star burst from top
      setTimeout(() => {
        confetti({
          particleCount: 80,
          spread: 360,
          origin: {
            y: 0.3
          },
          colors: ['#fbbf24', '#fcd34d', '#fde68a'],
          shapes: ['star'],
          scalar: 2
        });
      }, 250);

      // Final continuous fall
      setTimeout(() => {
        const duration = 1500;
        const end = Date.now() + duration;
        const frame = () => {
          confetti({
            particleCount: 3,
            angle: 60,
            spread: 55,
            origin: {
              x: 0,
              y: 0
            },
            colors: ['#10b981', '#14b8a6', '#06b6d4']
          });
          confetti({
            particleCount: 3,
            angle: 120,
            spread: 55,
            origin: {
              x: 1,
              y: 0
            },
            colors: ['#f59e0b', '#f97316', '#ef4444']
          });
          if (Date.now() < end) {
            requestAnimationFrame(frame);
          }
        };
        frame();
      }, 300);
    };
    celebrateSuccess();

    // Navigate to notes page with contact data
    let eventInfo = "To be determined";
    let times = "To be determined";
    if (data.eventDates && data.eventDates.length > 0) {
      const sortedEventDates = [...data.eventDates].sort((a, b) => a.date.getTime() - b.date.getTime());
      eventInfo = sortedEventDates.map(evt => `${format(evt.date, "MMM d, yyyy")}: ${evt.loadInTime ? `Load-in ${evt.loadInTime}, ` : ""}${evt.startTime} - ${evt.endTime}`).join(" | ");
    }

    // Mark inquiry as in progress so homepage doesn't reset session
    sessionStorage.setItem('inquiry_in_progress', 'true');

    // Small delay to let confetti animation start before navigation
    setTimeout(() => {
      console.log('🎯 [ContactForm] Navigating to /vibe-builder');
      navigate("/vibe-builder", {
        state: {
          contactData: {
            name: `${data.firstName} ${data.lastName}`,
            phone: data.phone,
            email: data.email,
            eventDate: eventInfo,
            eventTimes: times,
            venueAddress: data.venueAddress || "To be determined",
            userTimezone: getUserTimezone()
          },
          formData: data // Pass full form data for persistence
        }
      });
      console.log('✅ [ContactForm] Navigation triggered');
      setIsProcessing(false);
    }, 400);
  };
  if (isLoadingDraft) {
    return <TooltipProvider>
        <div className="space-y-8 bg-card/50 backdrop-blur-sm p-4 sm:p-6 lg:p-8 rounded-xl shadow-xl border border-border/30 max-w-5xl mx-auto animate-fade-in">
          <div className="space-y-4">
            <div className="h-8 w-48 bg-muted animate-pulse rounded-md"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="h-5 w-24 bg-muted animate-pulse rounded"></div>
                <div className="h-10 bg-muted animate-pulse rounded-md"></div>
              </div>
              <div className="space-y-2">
                <div className="h-5 w-24 bg-muted animate-pulse rounded"></div>
                <div className="h-10 bg-muted animate-pulse rounded-md"></div>
              </div>
            </div>
          </div>
          
          <div className="space-y-4 pt-6 border-t border-border/30">
            <div className="h-8 w-56 bg-muted animate-pulse rounded-md"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(i => <div key={i} className="space-y-2">
                  <div className="h-5 w-20 bg-muted animate-pulse rounded"></div>
                  <div className="h-10 bg-muted animate-pulse rounded-md"></div>
                </div>)}
            </div>
          </div>
          
          <div className="flex justify-center items-center py-8">
            <div className="flex flex-col items-center gap-3">
              <div className="h-8 w-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
              <p className="text-sm text-muted-foreground animate-pulse">Loading your form data...</p>
            </div>
          </div>
        </div>
      </TooltipProvider>;
  }
  return (
    <TooltipProvider>
      <Form {...form}>
        <form ref={formRef} onSubmit={form.handleSubmit(data => {
        console.log('📝 [ContactForm] Form submitted, validation passed');
        onNext(data);
      }, errors => {
        console.error('❌ [ContactForm] Form validation failed:', errors);
      })} className="space-y-8 bg-card/50 backdrop-blur-sm p-4 sm:p-6 lg:p-8 rounded-xl shadow-xl border border-border/30 max-w-5xl mx-auto">
          
          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className={cn("flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all duration-300", formStep === 1 ? "bg-primary text-primary-foreground" : "bg-emerald-500 text-white")}>
              {formStep === 1 ? "1" : "✓"}
            </div>
            <div className={cn("h-1 w-16 rounded-full transition-all duration-300", formStep === 2 ? "bg-primary" : "bg-border")}></div>
            <div className={cn("flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all duration-300", formStep === 2 ? "bg-primary text-primary-foreground" : "bg-border text-muted-foreground")}>
              2
            </div>
            <div className="ml-3 text-sm text-muted-foreground">
              {formStep === 1 ? "Contact Info" : "Event Details"}
            </div>
          </div>

          {/* STAGE 1: Basic Event & Contact Information (Lead Capture) */}
          {formStep === 1 && <>

              {/* Contact Information Section */}
              <div className="space-y-4 pt-6 border-t border-border/30">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold">Contact Information</h2>
                  <Tooltip>
                    <TooltipTrigger type="button">
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">We need to verify either your email or phone number to continue. Verification codes expire in 10 minutes.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField control={form.control} name="firstName" render={({
                field
              }) => <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="John" {...field} className="bg-background/50" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={form.control} name="lastName" render={({
                field
              }) => <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Smith" {...field} className="bg-background/50" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </div>

                <PhoneField form={form} />
                <EmailField form={form} />
                
                {form.formState.errors.emailVerified && <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-4">
                    <p className="text-sm text-destructive font-medium">
                      {form.formState.errors.emailVerified.message}
                    </p>
                  </div>}
              </div>

              {/* Stage 1 Continue Button */}
              <Button type="button" onClick={onStage1Complete} className="w-full mt-8 bg-gradient-to-r from-primary via-blue-600 to-purple-600 hover:from-primary/90 hover:via-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-2xl" size="lg">
                Continue to Event Details
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>}

          {/* STAGE 2: Detailed Event Information */}
          {formStep === 2 && <>
            {/* Event Dates & Times Section */}
            <div ref={eventDatesRef} className="space-y-4 pt-6 border-t border-border/30">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold">Event Dates & Times</h2>
                  <Tooltip>
                    <TooltipTrigger type="button">
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">Add up to 7 event dates. Start time is when your event begins for guests. End time is when the event concludes.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" size="sm" disabled={safeEventDates.length >= 7} className="w-full sm:w-auto bg-gradient-to-r from-primary/10 to-primary/5 hover:from-primary/20 hover:to-primary/10 border-primary/30 hover:border-primary/50 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-primary/20">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Date
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="multiple"
                      selected={safeEventDates.map(evt => evt.date)}
                      onSelect={dates => {
                        console.log("📅 [ContactForm] EventDates calendar onSelect raw:", dates);
                        try {
                          const normalizedDates = Array.isArray(dates)
                            ? dates.filter(d => d instanceof Date && !isNaN(d.getTime()))
                            : [];

                          if (normalizedDates.length === 0) {
                            form.setValue("eventDates", []);
                            return;
                          }

                          if (normalizedDates.length <= 7) {
                            const currentDates = (form.getValues("eventDates") || []) as any[];

                            const currentDateStrings = new Set(
                              currentDates
                                .map((evt: any) => {
                                  const raw = evt?.date;
                                  const d = raw instanceof Date ? raw : raw ? new Date(raw) : null;
                                  if (!d || isNaN(d.getTime())) return null;
                                  return format(d, "yyyy-MM-dd");
                                })
                                .filter((v): v is string => Boolean(v))
                            );

                            const newDates = normalizedDates.filter(date => {
                              const key = format(date, "yyyy-MM-dd");
                              return !currentDateStrings.has(key);
                            });

                            const selectedDateStrings = new Set(
                              normalizedDates.map(date => format(date, "yyyy-MM-dd"))
                            );

                            const remainingDates = currentDates.filter((evt: any) => {
                              const raw = evt?.date;
                              const d = raw instanceof Date ? raw : raw ? new Date(raw) : null;
                              if (!d || isNaN(d.getTime())) return false;
                              return selectedDateStrings.has(format(d, "yyyy-MM-dd"));
                            });

                            const newEventDates = newDates.map(date => {
                              const timeRange = getWeightedRandomTimeRange();
                              return {
                                date,
                                loadInTime: "",
                                startTime: timeRange.start,
                                endTime: timeRange.end,
                                venueAddress: "",
                                venueAddressVerified: false,
                                occasion: "",
                                guestCount: undefined,
                                vibes: [],
                                saved: false,
                                locked: false,
                                budgetLow: undefined,
                                budgetHigh: undefined,
                                indoorOutdoor: undefined,
                                travelAccommodation: undefined,
                              };
                            });

                            const allDates = [...remainingDates, ...newEventDates].sort((a, b) => {
                              const aRaw = a?.date;
                              const bRaw = b?.date;
                              const aDate = aRaw instanceof Date ? aRaw : aRaw ? new Date(aRaw) : new Date(0);
                              const bDate = bRaw instanceof Date ? bRaw : bRaw ? new Date(bRaw) : new Date(0);
                              return aDate.getTime() - bDate.getTime();
                            });

                            console.log("📅 [ContactForm] Updating eventDates to:", allDates);
                            form.setValue("eventDates", allDates);
                          }
                        } catch (error) {
                          console.error("❌ [ContactForm] Error in eventDates onSelect:", error);
                        }
                      }}
                      disabled={date => {
                        // Enforce 7-day minimum booking window
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const minDate = new Date(today);
                        minDate.setDate(minDate.getDate() + 7);
                        return date < minDate || safeEventDates.length >= 7;
                      }}
                      defaultMonth={(() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const firstAvailable = new Date(today);
                        firstAvailable.setDate(firstAvailable.getDate() + 7);
                        return firstAvailable;
                      })()}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {safeEventDates.length === 0 && <div className="relative overflow-hidden">
                  <div className="text-center p-12 bg-gradient-to-br from-primary/5 via-background to-primary/5 rounded-2xl border-2 border-dashed border-primary/30 hover:border-primary/50 transition-all duration-500 group animate-fade-in backdrop-blur-sm">
                    {/* Decorative background elements */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute -top-12 -right-12 w-32 h-32 bg-primary/5 rounded-full blur-3xl" />
                    <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />
                    
                    {/* Content */}
                    <div className="relative z-10">
                      <div className="inline-flex items-center justify-center p-4 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 mb-6 ring-4 ring-primary/10 group-hover:ring-primary/20 transition-all duration-300 group-hover:scale-110">
                        <CalendarIcon className="h-10 w-10 text-primary animate-pulse" />
                      </div>
                      
                      <h3 className="text-xl font-semibold mb-2 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                        Let's Add Your First Event Date
                      </h3>
                      
                      <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto leading-relaxed">
                        Click the <span className="font-semibold text-primary">Add Date</span> button above to select your event date(s). You can add up to 7 dates for multi-day events.
                      </p>
                      
                      {/* Visual indicators */}
                      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground/70">
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-primary/50 animate-pulse" />
                          <span>Quick & Easy</span>
                        </div>
                        <span className="text-muted-foreground/40">•</span>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-primary/50 animate-pulse" style={{ animationDelay: '0.5s' }} />
                          <span>Flexible Scheduling</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}

              <div className="space-y-4">
                {eventDates.sort((a, b) => a.date.getTime() - b.date.getTime()).map((eventDate, index) => <Card key={index} data-event-card className={cn("relative overflow-hidden border-2 transition-all duration-500 transform hover:scale-[1.02] hover:shadow-2xl animate-fade-in", "backdrop-blur-sm bg-gradient-to-br", eventDate.locked ? "from-emerald-500/20 via-emerald-400/10 to-cyan-500/20 border-emerald-400/40 shadow-emerald-500/20 shadow-lg" : "from-orange-500/20 via-amber-400/10 to-pink-500/20 border-orange-400/40 shadow-orange-500/20 shadow-lg hover:shadow-orange-500/40")}>
                    {/* Animated gradient border glow */}
                    <div className={cn("absolute inset-0 bg-gradient-to-r opacity-0 transition-opacity duration-500 hover:opacity-100 blur-xl -z-10", eventDate.locked ? "from-emerald-500/30 via-cyan-500/30 to-emerald-500/30" : "from-orange-500/30 via-amber-500/30 to-pink-500/30")} />
                    
                    {/* Status indicator bar with glow */}
                    <div className={cn("absolute left-0 top-0 bottom-0 w-1.5 transition-all duration-300", eventDate.locked ? "bg-gradient-to-b from-emerald-400 to-cyan-500 shadow-lg shadow-emerald-500/50" : "bg-gradient-to-b from-orange-400 to-pink-500 shadow-lg shadow-orange-500/50 animate-pulse")} />
                    
                    <div className="pl-5 pr-4 py-4 space-y-3">
                      {/* Header: Date and Actions */}
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <span className={cn("font-mono text-base font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r transition-all duration-300", eventDate.locked ? "from-emerald-400 to-cyan-400" : "from-orange-400 to-pink-400")}>
                            {format(eventDate.date, "MMM dd, yyyy")}
                          </span>
                          <div className={cn("h-2 w-2 rounded-full shrink-0 relative", eventDate.locked ? "bg-emerald-400 shadow-lg shadow-emerald-500/50" : "bg-orange-400 animate-pulse shadow-lg shadow-orange-500/50")}>
                            {!eventDate.locked && <div className="absolute inset-0 rounded-full bg-orange-400 animate-ping" />}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 shrink-0">
                          <Button type="button" variant={eventDate.locked ? "ghost" : "default"} size="sm" disabled={eventDate.locked || !eventDate.occasion || !eventDate.guestCount || !eventDate.vibes?.length || !eventDate.venueAddressVerified} className={cn("h-8 px-4 text-xs font-mono font-semibold transition-all duration-300 transform hover:scale-105", eventDate.locked ? "bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 hover:from-emerald-500/30 hover:to-cyan-500/30 text-emerald-400 border border-emerald-400/30" : "bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 shadow-lg shadow-orange-500/30")} onClick={e => {
                          if (!eventDate.locked) {
                            // Trigger confetti from the button position
                            const rect = e.currentTarget.getBoundingClientRect();
                            const x = (rect.left + rect.width / 2) / window.innerWidth;
                            const y = (rect.top + rect.height / 2) / window.innerHeight;

                            // Fire multiple confetti bursts with different colors
                            confetti({
                              particleCount: 100,
                              spread: 70,
                              origin: {
                                x,
                                y
                              },
                              colors: ['#10b981', '#14b8a6', '#06b6d4', '#0ea5e9'],
                              startVelocity: 45,
                              gravity: 1.2,
                              scalar: 1.2
                            });

                            // Second burst for extra effect
                            setTimeout(() => {
                              confetti({
                                particleCount: 50,
                                spread: 100,
                                origin: {
                                  x,
                                  y
                                },
                                colors: ['#34d399', '#22d3ee', '#38bdf8'],
                                startVelocity: 30
                              });
                            }, 150);
                          }
                          const currentDates = form.getValues("eventDates") || [];
                          currentDates[index].locked = true;
                          form.setValue("eventDates", currentDates);
                          toast({
                            title: "✓ Locked",
                            description: `Times for ${format(eventDate.date, "MMM d")} locked in.`
                          });
                        }}>
                            {eventDate.locked ? <>
                                <Check className="h-3 w-3 mr-1" />
                                Locked
                              </> : "Lock"}
                          </Button>
                          <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-destructive/20 hover:text-destructive hover:scale-110 transition-all duration-200 rounded-full" onClick={() => {
                          const currentDates = form.getValues("eventDates") || [];
                          form.setValue("eventDates", currentDates.filter((_, i) => i !== index));
                        }}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>

                      {/* Compact Time Slider */}
                      <div className="pt-2 px-1 bg-background/40 rounded-lg backdrop-blur-sm border border-border/30">
                        <TimeRangeSlider loadInTime={eventDate.loadInTime} startTime={eventDate.startTime} endTime={eventDate.endTime} onLoadInChange={value => {
                        const currentDates = form.getValues("eventDates") || [];
                        currentDates[index].loadInTime = value;
                        currentDates[index].locked = false;
                        form.setValue("eventDates", currentDates);
                      }} onStartChange={value => {
                        const currentDates = form.getValues("eventDates") || [];
                        currentDates[index].startTime = value;
                        currentDates[index].locked = false;
                        form.setValue("eventDates", currentDates);
                      }} onEndChange={value => {
                        const currentDates = form.getValues("eventDates") || [];
                        currentDates[index].endTime = value;
                        currentDates[index].locked = false;
                        form.setValue("eventDates", currentDates);
                      }} errors={{
                        loadIn: form.formState.errors.eventDates?.[index]?.loadInTime?.message,
                        start: form.formState.errors.eventDates?.[index]?.startTime?.message,
                        end: form.formState.errors.eventDates?.[index]?.endTime?.message
                      }} />
                      </div>

                      {/* Venue Address */}
                      <div className="pt-2">
                        <VenueAddressField form={form} fieldPrefix={`eventDates.${index}`} />
                      </div>

                      {/* Event Details Section */}
                      <div className="space-y-3 pt-2 border-t border-border/20">
                        {/* Occasion and Guest Count Row */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <FormField control={form.control} name={`eventDates.${index}.occasion` as const} render={({
                          field
                        }) => {
                          const [occasionOpen, setOccasionOpen] = React.useState(false);
                          return <FormItem>
                                <div className="flex items-center gap-2">
                                  <FormLabel className="text-sm font-medium">Occasion</FormLabel>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger type="button">
                                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                      </TooltipTrigger>
                                      <TooltipContent side="top" align="center" className="max-w-xs">
                                        <p>What type of event is this? Search to find your occasion.</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                                <Popover open={occasionOpen} onOpenChange={setOccasionOpen}>
                                  <PopoverTrigger asChild>
                                    <FormControl>
                                      <Button
                                        variant="outline"
                                        role="combobox"
                                        className={cn(
                                          "w-full justify-between bg-background/50 font-normal",
                                          !field.value && "text-muted-foreground"
                                        )}
                                      >
                                        {field.value || "Search occasion..."}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                      </Button>
                                    </FormControl>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0 z-50 bg-popover" align="start">
                                    <Command>
                                      <CommandInput placeholder="Search occasions..." />
                                      <CommandList className="max-h-[300px]">
                                        <CommandEmpty>No occasion found.</CommandEmpty>
                                        <CommandGroup>
                                          {OCCASIONS.map((occasion) => (
                                            <CommandItem
                                              key={occasion}
                                              value={occasion}
                                              onSelect={(currentValue) => {
                                                field.onChange(currentValue);
                                                const currentDates = form.getValues("eventDates") || [];
                                                currentDates[index].locked = false;
                                                form.setValue("eventDates", currentDates);
                                                setOccasionOpen(false);
                                              }}
                                            >
                                              <Check
                                                className={cn(
                                                  "mr-2 h-4 w-4",
                                                  occasion.toLowerCase() === field.value?.toLowerCase()
                                                    ? "opacity-100"
                                                    : "opacity-0"
                                                )}
                                              />
                                              {occasion}
                                            </CommandItem>
                                          ))}
                                        </CommandGroup>
                                      </CommandList>
                                    </Command>
                                  </PopoverContent>
                                </Popover>
                                <FormMessage />
                              </FormItem>
                        }} />

                          <FormField control={form.control} name={`eventDates.${index}.guestCount` as const} render={({
                          field
                        }) => <FormItem>
                                <div className="flex items-center gap-2">
                                  <FormLabel className="text-sm font-medium">Guest Count</FormLabel>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger type="button">
                                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                      </TooltipTrigger>
                                      <TooltipContent side="top" align="center" className="max-w-xs">
                                        <p>Approximate number of guests attending this event</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                                <FormControl>
                                  <Input 
                                    type="number" 
                                    placeholder="e.g., 150" 
                                    value={field.value || ""} 
                                    onChange={e => {
                                      const value = e.target.value === "" ? undefined : parseInt(e.target.value);
                                      field.onChange(value);
                                      const currentDates = form.getValues("eventDates") || [];
                                      currentDates[index].locked = false;
                                      form.setValue("eventDates", currentDates);
                                    }} 
                                    className="bg-background/50" 
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>} />
                        </div>

                        {/* Vibe/Theme */}
                        <FormField control={form.control} name={`eventDates.${index}.vibes` as const} render={({
                        field
                      }) => {
                        const [vibeOpen, setVibeOpen] = React.useState(false);
                        const [vibeSearch, setVibeSearch] = React.useState("");
                        const selectedVibes = (field.value as string[]) || [];
                        
                        return <FormItem>
                              <div className="flex items-center gap-2">
                                <FormLabel className="text-sm font-medium">Vibe / Theme / Inspo</FormLabel>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger type="button">
                                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent side="top" align="center" className="max-w-xs">
                                      <p>Share the atmosphere or aesthetic you're envisioning (select multiple)</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              
                              {/* Selected Vibes as Badges */}
                              {selectedVibes.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-2">
                                  {selectedVibes.map((vibe) => (
                                    <Badge
                                      key={vibe}
                                      variant="secondary"
                                      className="px-3 py-1 text-sm bg-gradient-to-r from-primary/20 to-primary/10 border-primary/30"
                                    >
                                      {vibe}
                                      <button
                                        type="button"
                                        className="ml-2 hover:text-destructive"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          const newVibes = selectedVibes.filter((v) => v !== vibe);
                                          field.onChange(newVibes);
                                          const currentDates = form.getValues("eventDates") || [];
                                          currentDates[index].locked = false;
                                          form.setValue("eventDates", currentDates);
                                        }}
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              
                              <Popover open={vibeOpen} onOpenChange={setVibeOpen}>
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button
                                      variant="outline"
                                      role="combobox"
                                      className={cn(
                                        "w-full justify-between bg-background/50 font-normal",
                                        selectedVibes.length === 0 && "text-muted-foreground"
                                      )}
                                    >
                                      {selectedVibes.length === 0 
                                        ? "Select vibes/themes..." 
                                        : `${selectedVibes.length} selected`}
                                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent className="w-[--radix-popover-trigger-width] p-0 z-50 bg-popover" align="start">
                                  <Command>
                                    <CommandInput 
                                      placeholder="Search or type custom vibe..." 
                                      value={vibeSearch}
                                      onValueChange={setVibeSearch}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          const input = vibeSearch.trim();
                                          if (input && !selectedVibes.some(v => v.toLowerCase() === input.toLowerCase())) {
                                            const newVibes = [...selectedVibes, input];
                                            field.onChange(newVibes);
                                            const currentDates = form.getValues("eventDates") || [];
                                            currentDates[index].locked = false;
                                            form.setValue("eventDates", currentDates);
                                            setVibeSearch('');
                                          }
                                        }
                                      }}
                                    />
                                    <CommandList className="max-h-[300px]">
                                      <CommandEmpty>No matching vibes. Press Enter to add custom.</CommandEmpty>
                                      <CommandGroup>
                                        {VIBES.map((vibe) => {
                                          const isSelected = selectedVibes.some(
                                            (v) => v.toLowerCase() === vibe.toLowerCase()
                                          );
                                          return (
                                            <CommandItem
                                              key={vibe}
                                              value={vibe}
                                            onSelect={() => {
                                                const newVibes = isSelected
                                                  ? selectedVibes.filter((v) => v.toLowerCase() !== vibe.toLowerCase())
                                                  : [...selectedVibes, vibe];
                                                field.onChange(newVibes);
                                                const currentDates = form.getValues("eventDates") || [];
                                                currentDates[index].locked = false;
                                                form.setValue("eventDates", currentDates);
                                                setVibeSearch('');
                                                setVibeOpen(false);
                                              }}
                                            >
                                              <Check
                                                className={cn(
                                                  "mr-2 h-4 w-4",
                                                  isSelected ? "opacity-100" : "opacity-0"
                                                )}
                                              />
                                              {vibe}
                                            </CommandItem>
                                          );
                                        })}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                              <FormMessage />
                            </FormItem>
                      }} />

                        {/* Budget & Logistics Section */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-border/20">
                          {/* Budget Range */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <FormLabel className="text-sm font-medium">Budget Range</FormLabel>
                              <Tooltip>
                                <TooltipTrigger type="button">
                                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">Your estimated budget range for live music at this event. This helps us recommend the best packages.</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <div className="flex items-center gap-2">
                              <FormField control={form.control} name={`eventDates.${index}.budgetLow` as const} render={({ field }) => (
                                <FormItem className="flex-1">
                                  <FormControl>
                                    <div className="relative">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                                      <Input 
                                        type="number" 
                                        placeholder="Low" 
                                        value={field.value || ""} 
                                        onChange={e => {
                                          const val = e.target.value === "" ? undefined : parseInt(e.target.value);
                                          field.onChange(val);
                                          const currentDates = form.getValues("eventDates") || [];
                                          currentDates[index].locked = false;
                                          form.setValue("eventDates", currentDates);
                                        }}
                                        className="bg-background/50 pl-7" 
                                      />
                                    </div>
                                  </FormControl>
                                </FormItem>
                              )} />
                              <span className="text-muted-foreground text-sm">–</span>
                              <FormField control={form.control} name={`eventDates.${index}.budgetHigh` as const} render={({ field }) => (
                                <FormItem className="flex-1">
                                  <FormControl>
                                    <div className="relative">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                                      <Input 
                                        type="number" 
                                        placeholder="High" 
                                        value={field.value || ""} 
                                        onChange={e => {
                                          const val = e.target.value === "" ? undefined : parseInt(e.target.value);
                                          field.onChange(val);
                                          const currentDates = form.getValues("eventDates") || [];
                                          currentDates[index].locked = false;
                                          form.setValue("eventDates", currentDates);
                                        }}
                                        className="bg-background/50 pl-7" 
                                      />
                                    </div>
                                  </FormControl>
                                </FormItem>
                              )} />
                            </div>
                          </div>

                          {/* Indoor / Outdoor */}
                          <FormField control={form.control} name={`eventDates.${index}.indoorOutdoor` as const} render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-2">
                                <FormLabel className="text-sm font-medium">Setting</FormLabel>
                              </div>
                              <FormControl>
                                <RadioGroup
                                  value={field.value || ""}
                                  onValueChange={(val) => {
                                    field.onChange(val);
                                    const currentDates = form.getValues("eventDates") || [];
                                    currentDates[index].locked = false;
                                    form.setValue("eventDates", currentDates);
                                  }}
                                  className="flex gap-3"
                                >
                                  {[
                                    { value: "indoor", label: "Indoor" },
                                    { value: "outdoor", label: "Outdoor" },
                                    { value: "both", label: "Both" },
                                  ].map((opt) => (
                                    <label
                                      key={opt.value}
                                      className={cn(
                                        "flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-all text-sm",
                                        field.value === opt.value
                                          ? "border-primary bg-primary/10 text-primary font-medium"
                                          : "border-border bg-background/50 hover:border-primary/40"
                                      )}
                                    >
                                      <RadioGroupItem value={opt.value} className="sr-only" />
                                      {opt.label}
                                    </label>
                                  ))}
                                </RadioGroup>
                              </FormControl>
                            </FormItem>
                          )} />
                        </div>

                        {/* Travel & Accommodations */}
                        <FormField control={form.control} name={`eventDates.${index}.travelAccommodation` as const} render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel className="text-sm font-medium">Travel & Accommodations</FormLabel>
                              <Tooltip>
                                <TooltipTrigger type="button">
                                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">Will you arrange travel, hotel, and meals for the regional manager and musicians? This affects our pricing.</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <FormControl>
                              <RadioGroup
                                value={field.value || ""}
                                onValueChange={(val) => {
                                  field.onChange(val);
                                  const currentDates = form.getValues("eventDates") || [];
                                  currentDates[index].locked = false;
                                  form.setValue("eventDates", currentDates);
                                }}
                                className="flex flex-col sm:flex-row gap-2"
                              >
                                {[
                                  { value: "yes", label: "Yes, we'll arrange", description: "Client covers travel" },
                                  { value: "depends", label: "Depends on budget", description: "Flexible" },
                                  { value: "no", label: "No, talent responsible", description: "Musicians arrange own" },
                                ].map((opt) => (
                                  <label
                                    key={opt.value}
                                    className={cn(
                                      "flex flex-col px-3 py-2 rounded-md border cursor-pointer transition-all flex-1",
                                      field.value === opt.value
                                        ? "border-primary bg-primary/10"
                                        : "border-border bg-background/50 hover:border-primary/40"
                                    )}
                                  >
                                    <div className="flex items-center gap-2">
                                      <RadioGroupItem value={opt.value} className="sr-only" />
                                      <span className={cn("text-sm", field.value === opt.value ? "font-medium text-primary" : "")}>{opt.label}</span>
                                    </div>
                                    <span className="text-xs text-muted-foreground">{opt.description}</span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </FormControl>
                          </FormItem>
                        )} />
                      </div>
                    </div>
                  </Card>)}
              </div>
              
              {form.formState.errors.eventDates && form.formState.isSubmitted && <p className="text-sm font-medium text-destructive">
                  At least one event date is required
                </p>}
            </div>

            {/* Stage 2 Navigation Buttons */}
            <Button type="submit" className="w-full mt-8 bg-gradient-to-r from-primary via-blue-600 to-purple-600 hover:from-primary/90 hover:via-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-2xl animate-fade-in" size="lg" disabled={isProcessing} onClick={() => console.log('👆 [ContactForm] Continue button clicked')}>
              {isProcessing ? <span className="flex items-center gap-2">
                  <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </span> : <>
                  Continue to Playlist Builder
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </>}
            </Button>

            <Button type="button" variant="ghost" onClick={() => {
              navigate("/?scrollTo=contact-section", {
                state: { formData: form.getValues(), returnToStep1: true }
              });
            }} className="w-full mt-2">
              ← Back to Contact Information
            </Button>

          </>}

      </form>
    </Form>
    </TooltipProvider>
  );
};
export default ContactForm;